import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import useScreen from '../../hooks/useScreen'
import Button from '../Button/Button'
import { ClockIcon, DownloadIcon } from '@heroicons/react/outline'
import { Link } from 'react-router-dom'
import TimeLineModal from './components/TimeLineModal'
import { useNetworkStatus } from '../../utils/networkStatus'

const TimeLineSteps = ({
    steps,
    title,
    isStepper,
    request,
    isDark,
    isAbsolute,
    setErrorMessage,
    setDownloadServiceError,
}) => {
    const { isMobile, isTablet } = useScreen()
    const { isOffline } = useNetworkStatus()

    const activeStep = steps?.find(s => s?.active === true || s?.isLast)
    const stepsFinished = steps?.filter(s => s?.finish === true)?.length
    const [modalTimeLineOpen, setModalTimeLineOpen] = useState(false)
    const parentWidthInitial = document.getElementById('contentLayout')
    const [width, setWidth] = useState(parentWidthInitial?.width)

    const countFinished = stepsFinished > 0 ? stepsFinished + 1 : 1

    const myObserve = new ResizeObserver(data => {
        if (data[0]) {
            setWidth(data[0]?.contentRect?.width)
        }
    })

    useEffect(() => {
        if (parentWidthInitial != null && isTablet && !isStepper) {
            myObserve.observe(parentWidthInitial)
        } else {
            myObserve.disconnect()
        }

        return () => {
            myObserve.disconnect()
        }
    }, [parentWidthInitial, isTablet])

    return (
        <div
            className={clsx(
                'h-auto flex flex-col items-start justify-start gap-y-4 lg:relative z-20 bottom-0 right-0 lg:border-t-0 border-grayLines',
                isAbsolute ? 'fixed' : 'relative',
                isDark ? 'bg-transparent' : 'bg-white',
                isStepper ? 'p-0' : 'lg:p-0 p-4 border-t',
            )}
            style={{
                width: isTablet && !isStepper ? width : '100%',
            }}
        >
            <TimeLineModal
                isOpen={modalTimeLineOpen}
                setIsOpen={setModalTimeLineOpen}
                steps={steps}
            />
            {title && (
                <h2 className="text-black90 font-semibold text-lg">{title}</h2>
            )}
            {(isMobile || isTablet) && activeStep && (
                <div className="w-full h-auto flex flex-row items-center justify-start">
                    <div className="w-full flex-grow h-auto flex flex-col items-start justify-start gap-y-1">
                        <p
                            className={clsx(
                                'font-medium text-base flex items-center justify-center gap-x-3',
                                isDark ? 'text-white' : 'text-textDarkerGray',
                            )}
                        >
                            {isStepper && (
                                <span className="w-8 h-8 rounded-full border border-black30 bg-black50 flex items-center justify-center">
                                    {countFinished}
                                </span>
                            )}
                            {activeStep?.name}
                        </p>
                        {isStepper && (isMobile || isTablet)
                            ? null
                            : activeStep?.description && (
                                  <p className="text-textDarkGray text-sm font-medium">
                                      {activeStep?.description}
                                  </p>
                              )}
                    </div>

                    {(isMobile || isTablet) && !isStepper && (
                        <div className="w-auto h-full flex items-center justify-center">
                            <Button
                                variant="tertiary"
                                className={
                                    !isOffline
                                        ? 'm-0 p-2 rounded-lg'
                                        : 'opacity-50'
                                }
                                type="button"
                                onClick={() => {
                                    !isOffline
                                        ? setModalTimeLineOpen(true)
                                        : setErrorMessage(true)
                                }}
                                icon={
                                    <ClockIcon className="w-6 h-6 m-0 text-textDarkerGray stroke-current" />
                                }
                            />
                        </div>
                    )}
                </div>
            )}
            {request?.hasReport === true && (isMobile || isTablet) ? null : (
                <div className="w-full h-auto flex flex-row items-start justify-between gap-x-4">
                    {steps?.map(step => {
                        return (
                            <div
                                key={step?.name}
                                className={clsx(
                                    'w-auto flex-grow flex flex-col items-start justify-start',
                                )}
                            >
                                <div className="w-full flex items-center justify-center">
                                    <span
                                        className={clsx(
                                            'h-0.5 transition-all',
                                            step?.finish && 'w-full',
                                            step?.active && 'w-1/2',
                                            !step?.finish &&
                                                !step?.active &&
                                                'w-full',
                                            step?.finish || step?.active
                                                ? 'bg-yellow'
                                                : 'bg-grayLines',
                                        )}
                                    />
                                    {step?.active && (
                                        <span className="h-0.5 w-1/2 transition-all bg-grayLines" />
                                    )}
                                </div>
                                <div className="w-full h-auto pt-4 flex-col items-start justify-center lg:flex hidden">
                                    <p className="text-textDarkerGray font-medium text-base lg:block hidden">
                                        {step?.name}
                                    </p>
                                    {step?.description && (
                                        <p className="text-textDarkGray font-medium text-sm lg:block hidden">
                                            {step?.description}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
            {request && request?.hasReport && (
                <Link
                    to={
                        !isOffline &&
                        '/getServiceReport?orderId=' + request?.salesforceId
                    }
                    className="w-full lg:w-auto lg:absolute relative lg:right-0 lg:-top-16"
                    target="_blank"
                >
                    <Button
                        text="Download Service Report"
                        variant="primary"
                        className="w-full lg:w-auto"
                        onClick={isOffline && setDownloadServiceError(true)}
                        icon={<DownloadIcon className="w-6 h-6 " />}
                    />
                </Link>
            )}
        </div>
    )
}

TimeLineSteps.propTypes = {
    steps: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            description: PropTypes.string,
            finish: PropTypes.bool,
            active: PropTypes.bool,
        }),
    ),
    title: PropTypes.string,
    isStepper: PropTypes.bool,
    request: PropTypes.oneOfType([PropTypes.object, PropTypes.any]),
    isDark: PropTypes.bool,
    showDescription: PropTypes.bool,
    isAbsolute: PropTypes.bool,
    setErrorMessage: PropTypes.func,
    setDownloadServiceError: PropTypes.func,
}

TimeLineSteps.defaultValues = {
    isStepper: false,
    isDark: false,
    showDescription: true,
    isAbsolute: true,
}

export default TimeLineSteps
